import React, { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AlertBanner } from '@spark';
import { PROVIDERS_MANAGED_BY_PLATFORM_HINT, useCanManageProviders } from '@/features/auth/providerAccess';

/**
 * Info banner shown when no commercial model providers are selectable,
 * linking to the provider keys tab in Account Settings.
 *
 * ``classificationOnlyConfigured``: the configured provider(s) are
 * classification-only decision models (TypeSafe AI) hidden on this task
 * type — the banner then explains that instead of the generic "no
 * providers" text.
 *
 * SaaS non-gods cannot reach Model Providers (the keys are SpecificAI's),
 * so for them the banner carries no settings link.
 */
const NoProvidersAlert = ({ classificationOnlyConfigured = false }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const canManageProviders = useCanManageProviders();

    const goToModelProvidersSettings = useCallback(() => {
        navigate('/settings?tab=providers', {
            state: { from: `${location.pathname}${location.search}${location.hash}` },
        });
    }, [navigate, location.pathname, location.search, location.hash]);

    const typeSafePrefix = classificationOnlyConfigured ? 'TypeSafe AI supports classification tasks only. ' : '';

    return (
        <AlertBanner
            feedback="info"
            showButton={false}
            showCloseButton={false}
            text={
                <span className="fs-unmask" style={{ fontSize: 13 }}>
                    {canManageProviders ? (
                        <>
                            {`${typeSafePrefix}To enable Model selection, add an API key or cloud credentials in `}
                            <a
                                onClick={(e) => {
                                    e.preventDefault();
                                    goToModelProvidersSettings();
                                }}
                                style={{
                                    color: 'var(--semantic-button-link-content-on-surface-default)',
                                    cursor: 'pointer',
                                }}
                            >
                                Account Settings
                            </a>
                            .
                        </>
                    ) : (
                        `${typeSafePrefix}${PROVIDERS_MANAGED_BY_PLATFORM_HINT}.`
                    )}
                </span>
            }
        />
    );
};

export default NoProvidersAlert;
